/**
 * Monitoring und Qualitätssicherung (Plan 05): Enums, Schemas, DTOs und die Regeln aus Abschnitt 6
 * (Anlage beim Abschluss, Fälligkeit und Überfälligkeit, Dringlichkeit, Abschlussfolgen, Vorschlag gesichert).
 */
import { z } from 'zod';
import type { RatingGrade } from './types.js';

export const MONITORING_KINDS = ['regular', 'additional', 'inquiry'] as const;
export type MonitoringKind = (typeof MONITORING_KINDS)[number];
export const MONITORING_KIND_LABELS: Record<MonitoringKind, string> = { regular: 'Regeltermin', additional: 'zusätzlich', inquiry: 'Nachfrage' };

export const MONITORING_STATUSES = ['planned', 'in_progress', 'done', 'skipped'] as const;
export type MonitoringStatus = (typeof MONITORING_STATUSES)[number];
export const MONITORING_STATUS_LABELS: Record<MonitoringStatus, string> = { planned: 'geplant', in_progress: 'in Bearbeitung', done: 'erledigt', skipped: 'entfallen' };

export const CONDITIONS = ['sehr_gut', 'gut', 'mittel', 'maessig', 'schlecht'] as const;
export type Condition = (typeof CONDITIONS)[number];
export const CONDITION_LABELS: Record<Condition, string> = { sehr_gut: 'sehr gut', gut: 'gut', mittel: 'mittel', maessig: 'mäßig', schlecht: 'schlecht' };

export const GROWTH_BEHAVIORS = ['sehr_gut_bis_gut', 'maessig', 'kuemmernd'] as const;
export type GrowthBehavior = (typeof GROWTH_BEHAVIORS)[number];
export const GROWTH_BEHAVIOR_LABELS: Record<GrowthBehavior, string> = { sehr_gut_bis_gut: 'sehr gut bis gut', maessig: 'mäßig', kuemmernd: 'kümmernd' };

export const WILDLIFE_STATES = ['ja', 'nein', 'teilweise', 'repariert', 'nicht_mehr_noetig', 'entfernt'] as const;
export type WildlifeState = (typeof WILDLIFE_STATES)[number];
export const WILDLIFE_LABELS: Record<WildlifeState, string> = { ja: 'ja', nein: 'nein', teilweise: 'teilweise', repariert: 'repariert', nicht_mehr_noetig: 'nicht mehr nötig', entfernt: 'entfernt' };

export const IMPROVEMENTS = ['nein', 'ja', 'eventuell', 'bereits_nachgebessert'] as const;
export type Improvement = (typeof IMPROVEMENTS)[number];
export const IMPROVEMENT_LABELS: Record<Improvement, string> = { nein: 'nein', ja: 'ja', eventuell: 'eventuell', bereits_nachgebessert: 'bereits nachgebessert' };

/** Excel-Freitexte auf Enums abbilden (Import, Seed); nicht Abbildbares bleibt null */
export function mapCondition(s: string | null | undefined): Condition | null {
  const t = (s ?? '').toLowerCase().trim();
  if (!t) return null;
  if (t.startsWith('sehr gut')) return 'sehr_gut';
  if (t.startsWith('gut')) return 'gut';
  if (t.startsWith('mittel')) return 'mittel';
  if (t.includes('schlecht') && !t.includes('mäßig')) return 'schlecht';
  if (t.includes('mäßig') || t.includes('maessig')) return 'maessig';
  return null;
}
export function mapWildlife(s: string | null | undefined): WildlifeState | null {
  const t = (s ?? '').toLowerCase().trim();
  if (!t) return null;
  if (t.includes('nicht mehr') || t.includes('kein') || t.includes('abgebaut') || t.includes('ohne')) return t.includes('abgebaut') ? 'entfernt' : 'nicht_mehr_noetig';
  if (t.includes('wieder intakt') || t.includes('repariert')) return 'repariert';
  if (t.includes('einzelsch') || t.includes('teilweise')) return 'teilweise';
  if (t.startsWith('nein')) return 'nein';
  if (t.startsWith('ja')) return 'ja';
  return null;
}
export function mapImprovement(s: string | null | undefined): Improvement | null {
  const t = (s ?? '').toLowerCase().trim();
  if (!t) return null;
  if (t.includes('bereits')) return 'bereits_nachgebessert';
  if (t.startsWith('nein')) return 'nein';
  if (t.includes('eventuell') || t.includes('evtl')) return 'eventuell';
  return 'ja';
}

/* ---------- Schemas ---------- */
const str = (n: number) => z.string().max(n).nullable().optional();
export const MonitoringSpeciesFailureSchema = z.object({ species_code: z.string().max(50), failure_rate_pct: z.number().min(0).max(100).nullable().optional(), note: z.string().max(200).nullable().optional() });
export type MonitoringSpeciesFailure = z.infer<typeof MonitoringSpeciesFailureSchema>;

export const MonitoringValuesSchema = z.object({
  done_at: str(10),
  done_by: str(60),
  condition: z.enum(CONDITIONS).nullable().optional(),
  condition_note: z.string().nullable().optional(),
  growth_success: z.string().nullable().optional(),
  failure_rate_pct: z.number().min(0).max(100).nullable().optional(),
  growth_behavior: z.enum(GROWTH_BEHAVIORS).nullable().optional(),
  wildlife_intact: z.enum(WILDLIFE_STATES).nullable().optional(),
  wildlife_note: z.string().nullable().optional(),
  improvement_needed: z.enum(IMPROVEMENTS).nullable().optional(),
  improvement_note: z.string().nullable().optional(),
  next_steps: z.string().nullable().optional(),
  agreements: z.string().nullable().optional(),
  remarks: z.string().nullable().optional(),
  secured: z.boolean().optional(),
  species_failures: z.array(MonitoringSpeciesFailureSchema).optional(),
});
export type MonitoringValues = z.infer<typeof MonitoringValuesSchema>;

/** PATCH /monitorings/:id: Zuständigkeit, Status in Bearbeitung, Werte als Entwurf */
export const MonitoringPatchSchema = MonitoringValuesSchema.extend({
  assigned_user_id: z.number().int().nullable().optional(),
  due_year: z.number().int().min(1990).max(2100).optional(),
  updated_at: z.string().optional(),
});
export type MonitoringPatch = z.infer<typeof MonitoringPatchSchema>;

/** POST /areas/:id/monitorings: zusätzliches Monitoring oder Nachfrage */
export const MonitoringCreateSchema = z.object({
  kind: z.enum(['additional', 'inquiry', 'regular']).default('additional'),
  due_year: z.number().int().min(1990).max(2100),
  assigned_user_id: z.number().int().nullable().optional(),
  remarks: z.string().nullable().optional(),
  origin_action_id: z.number().int().nullable().optional(),
});
export type MonitoringCreate = z.infer<typeof MonitoringCreateSchema>;

/** POST /monitorings/:id/complete: Befund plus Folgeentscheidung */
export const MonitoringCompleteSchema = MonitoringValuesSchema.extend({
  /** Zusätzliches Monitoring im Folgejahr anlegen (Standard ja bei Nachbesserung ja) */
  create_followup: z.boolean().optional(),
  updated_at: z.string().optional(),
});
export type MonitoringComplete = z.infer<typeof MonitoringCompleteSchema>;

export const MonitoringSkipSchema = z.object({ reason: z.string().min(1).max(500) });
export const MonitoringAssignSchema = z.object({ ids: z.array(z.number().int()).min(1).max(200), assigned_user_id: z.number().int().nullable() });

/** Offline-Upload (6.6): idempotent über client_uuid */
export const MonitoringSyncItemSchema = z.object({
  client_uuid: z.string().uuid(),
  monitoring_id: z.number().int().nullable().optional(),
  area_id: z.number().int().nullable().optional(),
  updated_at: z.string(),
  values: MonitoringCompleteSchema,
  complete: z.boolean().default(false),
});

/* ---------- DTOs ---------- */
export interface MonitoringListItem {
  id: number;
  area_id: number;
  area_name: string;
  area_status: string;
  city: string | null;
  state_code: string | null;
  state_label: string | null;
  region: string | null;
  lat: number | null;
  lng: number | null;
  planting_season_code: string | null;
  planting_year: number | null;
  partner: { id: number; display_name: string } | null;
  forester: { id: number; display_name: string; phone: string | null; mobile: string | null; email: string | null; rating_grade: RatingGrade | null } | null;
  origin_action: { id: number; code: string } | null;
  number: number;
  kind: MonitoringKind;
  kind_label: string;
  due_year: number;
  due_date: string;
  status: MonitoringStatus;
  status_label: string;
  /** Fälligkeit aus 6.2 */
  due: DueState;
  urgency: number;
  assigned: { id: number; display_name: string; initials: string | null } | null;
  done_at: string | null;
  done_by: string | null;
  condition: Condition | null;
  /** letzter erledigter Befund der Fläche */
  last: { year: number; condition: Condition | null; condition_label: string; improvement_needed: Improvement | null; wildlife_intact: WildlifeState | null } | null;
  photo_count: number;
  secured: boolean;
  review_needed: boolean;
  updated_at: string;
}
export interface MonitoringDetail extends MonitoringListItem {
  condition_note: string | null;
  growth_success: string | null;
  failure_rate_pct: number | null;
  growth_behavior: GrowthBehavior | null;
  wildlife_intact: WildlifeState | null;
  wildlife_note: string | null;
  improvement_needed: Improvement | null;
  improvement_note: string | null;
  next_steps: string | null;
  agreements: string | null;
  remarks: string | null;
  species_failures: (MonitoringSpeciesFailure & { label: string })[];
  /** Baumarten der Ursprungsaktion für die Vorbelegung */
  action_species: { species_code: string; label: string; count: number }[];
  photos: { id: number; original_name: string; has_thumb: boolean }[];
  skip_reason: string | null;
  area_hectares: number | null;
  area_trees: number | null;
  area_secured_at: string | null;
  /** Vorschau der Folge (6.3): nächster Regeltermin oder Nachfragejahre */
  next_preview: NextPreview;
  created_at: string;
  /** Einstellungen für Zeitfenster und Fristen */
  settings?: MonitoringSettings;
}
export interface MonitoringGroup {
  state_code: string | null;
  state_label: string;
  count: number;
  hotels: { id: number; display_name: string; rating_grade: RatingGrade | null; city: string | null }[];
}
export interface MonitoringListResponse {
  items: MonitoringListItem[];
  groups: MonitoringGroup[];
  year: number;
  counters: { all: number; due: number; overdue: number; in_progress: number; done: number; inquiry: number; secure_candidates: number };
  facets: { state: { value: string; count: number }[]; assigned: { value: string; label: string; count: number }[] };
  settings?: MonitoringSettings;
}
export interface MonitoringSummary {
  year: number;
  in_monitoring: number;
  secured: number;
  due: number;
  overdue: number;
  done: number;
  inquiries_due: number;
  secure_candidates: number;
}
export interface SecureCandidate {
  id: number;
  name: string;
  state_label: string | null;
  region: string | null;
  planting_year: number | null;
  age_years: number;
  last: { year: number; condition_label: string } | null;
  partner: string | null;
}

/* ---------- Regeln ---------- */
export interface MonitoringSettings {
  intervals: number[];
  window: { from_month: number; to_month: number };
  inquiryYearly: boolean;
  secureAfterYears: number;
}
export const DEFAULT_MONITORING_SETTINGS: MonitoringSettings = { intervals: [2, 5], window: { from_month: 3, to_month: 9 }, inquiryYearly: true, secureAfterYears: 10 };

export const dueDateOf = (year: number, fromMonth = 3) => `${year}-${String(fromMonth).padStart(2, '0')}-01`;
export const windowLabel = (w: MonitoringSettings['window'] = DEFAULT_MONITORING_SETTINGS.window) => `${MONTHS[w.from_month - 1]} bis ${MONTHS[w.to_month - 1]}`;
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/**
 * 6.1 Anlage beim Abschluss einer Pflanzaktion: Regeltermine je Intervall, Nachfragen in den Zwischenjahren.
 * `existingYears` verhindert Duplikate für dasselbe Fälligkeitsjahr (zweite Pflanzung auf derselben Fläche).
 */
export function planMonitorings(plantingYear: number, existing: { due_year: number; kind: MonitoringKind }[], s: MonitoringSettings = DEFAULT_MONITORING_SETTINGS): { kind: MonitoringKind; due_year: number; number: number }[] {
  const intervals = [...new Set(s.intervals.filter((i) => i > 0))].sort((a, b) => a - b);
  if (!intervals.length) return [];
  const taken = new Set(existing.map((e) => e.due_year));
  const out: { kind: MonitoringKind; due_year: number; number: number }[] = [];
  const regularYears = intervals.map((i) => plantingYear + i);
  let number = existing.filter((e) => e.kind !== 'inquiry').length;
  for (const y of regularYears) {
    if (taken.has(y)) continue;
    out.push({ kind: 'regular', due_year: y, number: ++number });
    taken.add(y);
  }
  if (s.inquiryYearly) {
    const last = regularYears[regularYears.length - 1]!;
    for (let y = plantingYear + 1; y < last; y++) {
      if (regularYears.includes(y) || taken.has(y)) continue;
      out.push({ kind: 'inquiry', due_year: y, number: 0 });
      taken.add(y);
    }
  }
  return out.sort((a, b) => a.due_year - b.due_year);
}

export interface DueState {
  kind: 'overdue' | 'due' | 'upcoming' | 'done' | 'skipped';
  label: string;
  /** Ende des Fensters (30.09. des Fälligkeitsjahrs) */
  window_end: string;
}
/** 6.2 Fälligkeit: fällig im Jahr X, überfällig nach dem Ende des Fensters oder wenn das Jahr vorbei ist */
export function dueState(m: { due_year: number; status: MonitoringStatus }, today: string, w: MonitoringSettings['window'] = DEFAULT_MONITORING_SETTINGS.window): DueState {
  const end = lastDayOfMonth(m.due_year, w.to_month);
  if (m.status === 'done') return { kind: 'done', label: 'erledigt', window_end: end };
  if (m.status === 'skipped') return { kind: 'skipped', label: 'entfallen', window_end: end };
  const year = Number(today.slice(0, 4));
  if (m.due_year < year || (m.due_year === year && today > end)) return { kind: 'overdue', label: 'überfällig', window_end: end };
  if (m.due_year === year) return { kind: 'due', label: `fällig ${m.due_year}`, window_end: end };
  return { kind: 'upcoming', label: `fällig ${m.due_year}`, window_end: end };
}
export function lastDayOfMonth(year: number, month: number): string {
  const d = new Date(Date.UTC(year, month, 0));
  return d.toISOString().slice(0, 10);
}

/** 6.2 Dringlichkeit: überfällig > fällig mit Nachbesserung im letzten Befund > fällig dieses Jahr > nächstes Jahr */
export function urgencyOf(due: DueState, lastImprovement: Improvement | null | undefined): number {
  if (due.kind === 'overdue') return 0;
  if (due.kind === 'due') return lastImprovement === 'ja' || lastImprovement === 'eventuell' ? 1 : 2;
  if (due.kind === 'upcoming') return 3;
  return 9;
}
export function compareMonitorings(a: { urgency: number; due_year: number; region: string | null; area_name: string }, b: typeof a): number {
  return a.urgency - b.urgency || a.due_year - b.due_year || (a.region ?? '').localeCompare(b.region ?? '', 'de') || a.area_name.localeCompare(b.area_name, 'de');
}

export interface NextPreview {
  /** nächster Regeltermin, wenn noch einer folgt */
  next_regular: number | null;
  /** Jahre mit Nachfrage bis zum letzten Regeltermin */
  inquiry_years: number[];
  label: string;
}
/**
 * 6.3 Vorschau der Folge nach Abschluss des aktuellen Monitorings. Für importierte Altflächen ohne vollständige Reihe gilt:
 * nach dem n-ten Monitoring folgt der Regeltermin nach intervals[n] Jahren ab Pflanzjahr, wenn noch nicht vorhanden.
 */
export function nextPreview(p: { planting_year: number | null; current_due_year: number; current_kind: MonitoringKind; open: { due_year: number; kind: MonitoringKind }[]; done_regular_count: number; secured: boolean }, s: MonitoringSettings = DEFAULT_MONITORING_SETTINGS): NextPreview {
  if (p.secured) return { next_regular: null, inquiry_years: [], label: 'Kultur gesichert, Fläche wird aus dem Monitoring genommen' };
  const openRegular = p.open.filter((o) => o.kind !== 'inquiry' && o.due_year > p.current_due_year).map((o) => o.due_year).sort((a, b) => a - b);
  let next = openRegular[0] ?? null;
  if (next == null && p.planting_year != null && p.current_kind !== 'inquiry') {
    const idx = p.done_regular_count + 1; // dieses Monitoring gilt nach dem Abschluss als erledigt
    const interval = s.intervals[idx];
    if (interval != null && p.planting_year + interval > p.current_due_year) next = p.planting_year + interval;
  }
  const lastRegular = p.planting_year != null ? p.planting_year + Math.max(...s.intervals) : null;
  const inquiry = s.inquiryYearly && lastRegular != null ? p.open.filter((o) => o.kind === 'inquiry' && o.due_year > p.current_due_year).map((o) => o.due_year).sort((a, b) => a - b) : [];
  const yearsAfter = p.planting_year != null && next != null ? `${next - p.planting_year} Jahre nach Pflanzung` : null;
  const label = next != null ? `Nächstes Monitoring: ${next}${yearsAfter ? ` (${yearsAfter})` : ''}` : inquiry.length ? `Kein weiteres Regelmonitoring, jährliche Nachfrage bis ${inquiry[inquiry.length - 1]}` : 'Kein weiteres Regelmonitoring geplant';
  return { next_regular: next, inquiry_years: inquiry, label };
}

/** 6.4 Vorschlag gesichert: Pflanzjahr plus n Jahre erreicht, im Monitoring, nicht gesichert */
export function isSecureCandidate(a: { planting_year: number | null; status: string; secured_at: string | null }, todayYear: number, secureAfterYears = 10): boolean {
  if (!a.planting_year || a.secured_at) return false;
  if (!['bepflanzt', 'im_monitoring'].includes(a.status)) return false;
  return a.planting_year + secureAfterYears <= todayYear;
}

/** Pflicht beim Abschluss (5.3): Datum, durch, Wildschutz, Zustand */
export function validateMonitoringComplete(v: Partial<MonitoringValues>): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!v.done_at) errors.done_at = 'Datum fehlt';
  if (!v.done_by?.trim()) errors.done_by = 'Durchgeführt von fehlt';
  if (!v.wildlife_intact) errors.wildlife_intact = 'Wildschutz angeben';
  if (!v.condition) errors.condition = 'Flächenzustand angeben';
  if (v.failure_rate_pct != null && (v.failure_rate_pct < 0 || v.failure_rate_pct > 100)) errors.failure_rate_pct = 'Prozentwert zwischen 0 und 100';
  return errors;
}

/** Abschnitte der Erfassungsseite (5.3) mit Feldern für Zähler und Druckansicht */
export const MONITORING_SECTIONS: { key: string; title: string; fields: (keyof MonitoringValues)[] }[] = [
  { key: 'kopf', title: 'Termin', fields: ['done_at', 'done_by'] },
  { key: 'wildschutz', title: 'Wildschutz', fields: ['wildlife_intact', 'wildlife_note'] },
  { key: 'anwuchs', title: 'Anwuchs', fields: ['failure_rate_pct', 'species_failures', 'growth_success'] },
  { key: 'zustand', title: 'Wuchs und Zustand', fields: ['growth_behavior', 'condition', 'condition_note'] },
  { key: 'nachbesserung', title: 'Nachbesserung', fields: ['improvement_needed', 'improvement_note', 'next_steps'] },
  { key: 'vereinbarungen', title: 'Vereinbarungen', fields: ['agreements', 'remarks'] },
];

/** Vorbereitete Nachfrage-E-Mail an den Förster (M-06); Versand aus dem System folgt mit Plan 06 */
export function inquiryMailDraft(p: { foresterName: string | null; areaName: string; plantingYear: number | null; year: number; senderName: string | null }): { subject: string; body: string } {
  const subject = `Nachfrage Kulturzustand ${p.areaName}${p.plantingYear ? ` (Pflanzung ${p.plantingYear})` : ''}`;
  const body = [
    `Guten Tag${p.foresterName ? ` ${p.foresterName}` : ''},`,
    '',
    `für unsere jährliche Nachfrage ${p.year} bitten wir um eine kurze Rückmeldung zur Fläche „${p.areaName}“${p.plantingYear ? ` (Pflanzung ${p.plantingYear})` : ''}:`,
    '',
    '- Ist der Wildschutz intakt?',
    '- Wie ist der Anwuchserfolg, gibt es nennenswerte Ausfälle (ggf. je Baumart)?',
    '- Ist eine Nachbesserung erforderlich?',
    '- Gibt es sonstige Vereinbarungen oder Hinweise?',
    '',
    'Gern auch mit ein bis zwei Fotos.',
    '',
    'Vielen Dank und freundliche Grüße',
    p.senderName ?? 'Trinkwasserwald e.V.',
  ].join('\n');
  return { subject, body };
}
